import { useEffect } from 'react';
import { useFindAccount } from '@features/auth/hooks/useFindAccount';
import Button from '@shared/components/atoms/Button';
import styles from '@pages/login/components/FindAccount/FindAccount.module.scss';

/**
 * FindAccount
 * - 아이디 찾기 / 비밀번호 찾기 공용 컴포넌트
 * - 비즈니스 로직은 useFindAccount 훅으로 위임
 */
export default function FindAccount({
  mode,
  companyList = [],
  resetSignal,
  onSuccess,
  onError,
  onCancel,
  onResendSuccess,
  loginUserId,
  loginCompanyCode,
}) {
  const { state, actions } = useFindAccount({
    mode,
    onSuccess,
    onError,
    onResendSuccess,
    loginUserId,
    loginCompanyCode,
  });

  /** =======================
   * 모달 초기화 신호 처리
   ======================= */
  useEffect(() => {
    actions.reset();
  }, [resetSignal]);

  /** =======================
   * UI
   ======================= */
  return (
    <div className={styles.container}>
      {mode === 'emailVerification' && (
        <div className={styles.noticeRow}>
          <div>· 추가 인증이 필요합니다.</div>
          <div>· 로그인 하시려면 이메일 인증을 진행해 주세요.</div>
        </div>
      )}

      <div className={styles.card}>
        {mode === 'findPassword' && (
          <>
            <div className={styles.formRow}>
              <label>회사</label>
              <select
                value={state.companyCode}
                onChange={(e) => actions.setCompanyCode(e.target.value)}
              >
                <option value="">회사 선택</option>
                {companyList.map((company) => (
                  <option key={company.companyCode} value={company.companyCode}>
                    {company.company}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.formRow}>
              <label>로그인 아이디</label>
              <input value={state.loginId} onChange={(e) => actions.setLoginId(e.target.value)} />
            </div>
          </>
        )}

        {mode !== 'emailVerification' && (
          <div className={styles.formRow}>
            <label>이름</label>
            <input value={state.name} onChange={(e) => actions.setName(e.target.value)} />
          </div>
        )}

        <div className={`${styles.formRow} ${styles.emailRow}`}>
          <label>사내 이메일 주소</label>
          <input value={state.email} onChange={(e) => actions.setEmail(e.target.value)} />
          <button
            type="button"
            onClick={actions.handleSendCode}
            disabled={state.isSending || (state.isCodeSent && state.resendRemaining > 0)}
          >
            {state.isSending ? '전송 중...' : '인증번호 받기'}
          </button>
        </div>

        <div className={`${styles.formRow} ${styles.codeRow}`}>
          <label></label>
          <div className={styles.codeInput}>
            <input
              placeholder="인증번호 6자리"
              value={state.code}
              onChange={(e) => actions.setCode(e.target.value)}
            />
            {mode === 'emailVerification' && (
              <div className={styles.noticeRow}>
                ※ 인증 메일은 일반적으로 수 분 내 도착하나, <br />
                메일 서버 상황에 따라 예상보다 지연될 수 있습니다. <br />
                메일이 도착하지 않을 경우 스팸함을 확인하거나 ‘인증메일 재전송’을 이용해 주세요.
              </div>
            )}
          </div>

          {state.isCodeSent && (
            <div className={styles.timer}>
              {state.remaining > 0 ? actions.formatTime(state.remaining) : '인증시간 만료'}
              <button
                type="button"
                className={styles.resend}
                onClick={actions.handleSendCode}
                disabled={state.isSending || state.resendRemaining > 0}
              >
                {state.resendRemaining > 0
                  ? `재전송 (${state.resendRemaining}s)`
                  : '인증메일 재전송'}
              </button>
            </div>
          )}
        </div>
      </div>
      <div className={styles.buttonArea}>
        <Button onClick={onCancel} className={styles.cancel}>
          취소
        </Button>
        <Button
          type="button"
          onClick={() => {
            if (mode === 'emailVerification') {
              onSuccess({
                emailCode: state.code,
                requestId: state.requestId,
              });
            } else {
              actions.handleVerify();
            }
          }}
          disabled={state.isVerifying || !state.isCodeSent}
          className={styles.confirm}
        >
          {state.isVerifying ? '확인 중...' : '확인'}
        </Button>
      </div>
    </div>
  );
}
